import * as React from "react";
import { useForm } from "react-hook-form";
import { useMappedState } from "redux-react-hook";
import { toast } from "react-toastify";
import _ from "lodash";

import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text/Text";
import Input from "src/library/components/Forms/Input";
import {
  FormRow,
  TextArea
} from "src/library/components/Forms";
import {
  useSupportApi,
  CreateSupportTicketParams
} from "src/modules/support/api/support.api";
import type { IStore } from "src/core/shared/store";
import useDrop from "src/core/drops/hooks/useDrop";

type SupportTicketFormValues = Omit<CreateSupportTicketParams, "userId" | "supportTicketTypeId">;

/**
 * Simple form to allow a user to submit a support ticket
 * Currently just feedback tickets
 */
const SupportTicketForm: React.ReactFCWithChildren = () => {
  const { user, supportTicketTypes } = useMappedState(({ global }: IStore) => ({
    user: global.user,
    supportTicketTypes: global?.settings?.supportTicketTypes || []
  }));

  const supportTicketTypeId = React.useMemo(() => {
    const ticketType = _.find(supportTicketTypes, type => type.name === "Provide feedback");

    return ticketType ? ticketType.id : null;
  }, [ supportTicketTypes ]);

  const [ submitFeedbackRes, submitFeedbackReq ] = useSupportApi("CREATE_SUPPORT_TICKET");
  const { handleSubmit, register } = useForm<SupportTicketFormValues>();
  const [ showSuccessMessage, setShowSuccessMessage ] = React.useState(false);
  const [ { drop } ] = useDrop();

  if (!supportTicketTypeId) {
    return null;
  }

  const submitForm = React.useCallback((values: Omit<SupportTicketFormValues, "branchId">) => {
    if (user && drop) {
      submitFeedbackReq({
        userId: user.id,
        supportTicketTypeId,
        branchId: drop.id,
        ...values
      });
    }
  }, [ user, drop ]);

  React.useEffect(() => {
    if (submitFeedbackRes.data) {
      setShowSuccessMessage(true);
    }

    if (submitFeedbackRes.error) {
      toast.error(submitFeedbackRes.error.message);
    }
  }, [ submitFeedbackRes.data, submitFeedbackRes.error ]);

  return (
    <>
      {showSuccessMessage ? (
        <Text>
          Thank you for your feedback!
        </Text>
      ) :
        (
          <>
            <Text as="h2">
              Feedback
            </Text>

            <form onSubmit={handleSubmit(values => submitForm(values))}>
              <Text as="p">
                We'd love to hear your views about our online ordering portal. We are always looking to improve and make things easier for our customers. If you have had a problem using Mezze or have an idea of how to improve it let us know using the form below.
              </Text>

              <FormRow>
                <Input
                  name="subject"
                  label="Subject"
                  as="input"
                  ref={register(({ required: true }))}
                />
              </FormRow>

              <FormRow>
                <TextArea
                  name="body"
                  label="Comments"
                  as="textarea"
                  ref={register(({ required: true }))}
                />
              </FormRow>

              <FormRow>
                <Button type="submit">
                  Submit feedback
                </Button>
              </FormRow>
            </form>
          </>
        )}
    </>
  );
};

export default SupportTicketForm;
